import Autotimer from 'autotimer';
import Fuse from 'fuse.js';
import humanTime from 'humantime';
import Noty from 'noty';
import Jamments from 'jamments-front';

import {
    ready,
    find,
    findAll,
    findChild,
    findChildren,
    on,
    onAll,
    once,
    off,
    preventDefault,
    getCoordinates,
    getParent,
    getValue,
    setValue,
    getEventTarget,
    getAttribute,
    getWidth,
    getHeight,
    prepend,
    setHTML,
    setStyle,
    addClass,
    hasClass,
    removeClass
} from 'flightdom';

const timer = new Autotimer();
const getTrimmedValue = el => getValue(el).trim();
const jamments = new Jamments({ endpoint: 'https:///buzut.alwaysdata.net', cachedFilesURI: 'static' });

function handleError(err) {
    console.error(err);
}

const showErrNotif = msg => new Noty({ theme: 'sunset', type: 'error', text: msg }).show();
const showSuccessNotif = msg => new Noty({ theme: 'sunset', type: 'success', text: msg }).show();
const showGenericErrMsg = () => showErrNotif('Une erreur inconnue s’est produite.');

/**
 * Fetch json data from given url
 * @param { String } url
 * @return { Promise }
 */
function getJsonData(url) {
    return fetch(url)
    .then((res) => {
        if (!res.ok) return Promise.reject(new Error(res.status));
        return res.json();
    });
}

/**
 * Init search: get data, search and display results on input events
 * @param { HTMLInputElement } searchInput
 * @param { HTMLElement } resultsList
 */
function initSearch(searchInput, resultsList) {
    const initSearchListener = (fuse) => {
        const showResults = () => addClass(resultsList, 'active');
        const hideResults = () => removeClass(resultsList, 'active');

        on(searchInput, 'input', (e) => {
            timer(200, () => {
                const searchTerm = getTrimmedValue(e.target);

                if (searchTerm.length < 2) {
                    hideResults();
                    return;
                }

                const results = fuse.search(searchTerm);

                let html = results.length ? '' : '<li>Je n’ai rien trouvé pour votre requête 🙈</li>';
                for (let i = 0; i < 20 && i < results.length; i++) {
                    html += `<li><a href="${results[i].permalink}">${results[i].title}</a></li>`;
                }

                setHTML(resultsList, html);
                showResults();
            });
        });

        // without timer, results disappear even before click
        on(searchInput, 'blur', () => timer(50, hideResults));
    };

    getJsonData('/api.json')
    .then(articles => initSearchListener(new Fuse([...articles.posts, ...articles.pages], { threshold: 0.3, keys: ['title', 'tags.name', 'keywords'] })))
    .catch(handleError);
}

/**
 * Save user infos
 * @param { Object } user
 * @param { String } [user.name]
 * @param { String } [user.email]
 * @param { String } [user.md5Email]
 * @param { String } [user.secret]
 */
function saveUserLocally(user) {
    Object.keys(user).forEach(key => localStorage.setItem(key, user[key]));
}

/**
 * Compare email hashes to determine if current user is admin
 * @param { String } md5AdminEmail
 * @param { String } md5UserEmail
 * @return { Boolean }
 */
function checkIfUserIsAdmin(md5AdminEmail, md5UserEmail) {
    if (!md5AdminEmail || !md5UserEmail) return false;
    if (md5AdminEmail !== md5UserEmail) return false;
    return true;
}

/**
 * Prefill comment form if user data exist in localStorage
 * @param { HTMLInputElement } nameInput
 * @param { HTMLInputElement } emailInput
 */
function preFillCommentForm(nameInput, emailInput) {
    const name = localStorage.getItem('name');
    const email = localStorage.getItem('email');

    if (name && email) {
        setValue(nameInput, name);
        setValue(emailInput, email);
    }
}

/**
 * Fetch comments and insert them on the page
 * @param { String } md5Email
 * @param { String } secret
 * @param { Boolean } isAdmin
 * @param { String } slug
 * @param { HTMLElement } commentSection
 * @return { Promise }
 */
function insertComments(md5Email, secret, isAdmin, slug, commentSection) {
    if (!commentSection) return Promise.resolve();

    return jamments.getComments(slug)
    .then((comments) => {
        let commentsMarkup = '';
        const createCommentMarkup = (comment) => {
            let controls = '';
            if (isAdmin && secret) controls = `<button class="edit" data-id="${comment.id}"></button><button class="delete" data-id="${comment.id}"></button>`;
            else if (md5Email && secret && md5Email === comment.md5_email) controls = `<button class="edit" data-id="${comment.id}"></button>`;

            commentsMarkup += `<article>
            <header>
                <img class="avatar" src="https://www.gravatar.com/avatar/${comment.md5_email}">
                <h1>${comment.name} dit&nbsp;–</h1>
                <time datetime="${comment.submitted_at}">${humanTime(comment.submitted_at)}</time>
                ${controls}
            </header>
            <p>${Jamments.parseMd(Jamments.escapeHtml(comment.comment))}</p>
            <button class="answer" data-id="${comment.id}">💬 Répondre</button>`;

            if (comment.children.length) comment.children.forEach(createCommentMarkup);

            commentsMarkup += '</article>';
        };

        comments.forEach(createCommentMarkup);
        setHTML(commentSection, commentsMarkup);

        return Promise.resolve();
    })
    .catch((err) => {
        if (err.message === 404) return Promise.resolve();
        return err;
    });
}

/**
 * Add comment edit/delete behaviour
 * @param { HTMLElement } commentSection
 * @param { HTMLFormElement } commentForm
 * @param { HTMLInputElement } commentInput
 * @param { String } secret
 */
function initCommentsControls(commentSection, commentForm, commentInput, secret) {
    onAll(findChildren(commentSection, '.edit'), 'click', (e) => {
        const commentId = getAttribute(e.target, 'data-id');
        const sendBtn = findChild(commentForm, '.send');
        const cancelBtn = findChild(commentForm, '.cancel-edit');

        addClass(commentForm, 'editing');
        setHTML(sendBtn, 'Modifier le commentaire');
        setStyle(cancelBtn, 'display', 'inline-block');
        setValue(commentInput, jamments.getCommentById(commentId).comment);

        commentForm.scrollIntoView({ behavior: 'smooth' });

        const resetFormToNormal = () => {
            setValue(commentInput, '');
            removeClass(commentForm, 'editing');
            setStyle(cancelBtn, 'display', 'none');
            setHTML(sendBtn, 'Soumettre le commentaire');
        };

        const submitUpdate = (ev) => {
            ev.preventDefault();
            const comment = getTrimmedValue(commentInput);

            jamments.updateComment(commentId, secret, comment)
            .then(() => {
                resetFormToNormal();
                off(commentForm, 'submit', submitUpdate);
                showSuccessNotif('Votre commentaire a bien été modifié');
                commentInput.value = '';
            })
            .catch(showGenericErrMsg);
        };

        on(commentForm, 'submit', submitUpdate);

        once(cancelBtn, 'click', (ev) => {
            ev.preventDefault();

            resetFormToNormal();
            off(commentForm, 'submit', submitUpdate);
        });
    });

    onAll(findChildren(commentSection, '.delete'), 'click', (e) => {
        const commentId = getAttribute(e.target, 'data-id');

        const deleteModal = new Noty({
            text: 'Es-tu certain de vouloir supprimer ce commentaire ?',
            buttons: [
                Noty.button('Oui', 'noty noty-danger', () => {
                    deleteModal.close();

                    jamments.deleteComment(commentId, secret)
                    .then(() => {
                        getParent(getParent(e.target)).remove();
                        showSuccessNotif('Le commentaire a bien été supprimé !');
                    })
                    .catch(showGenericErrMsg);
                }),

                Noty.button('Non', 'noty noty-neutral', () => deleteModal.close())
            ]
        }).show();
    });
}

/**
 * Bind comment form listener and send comment on user validation
 * @param { String } slug
 * @param { HTMLElement } commentSection
 * @param { HTMLFormElement } commentForm
 * @param { HTMLInputElement } commentInput
 * @param { HTMLInputElement } nameInput
 * @param { HTMLInputElement } emailInput
 */
function initPostComment(slug, commentSection, commentForm, commentInput, nameInput, emailInput) {
    let parentId;

    onAll(findChildren(commentSection, '.answer'), 'click', (e) => {
        parentId = getAttribute(e.target, 'data-id');
        commentForm.scrollIntoView({ behavior: 'smooth' });
    });

    on(commentForm, 'submit', (e) => {
        e.preventDefault();

        if (hasClass(commentForm, 'editing')) return;

        const comment = getTrimmedValue(commentInput);
        const name = getTrimmedValue(nameInput);
        const email = getTrimmedValue(emailInput);

        if (
            !slug
            || !commentForm.reportValidity()
            || !comment || comment.length < 3
            || !name || name.length < 2
            || !email || email.length < 5 || !emailInput.checkValidity()
        ) {
            showErrNotif('Tous les champs doivent être remplis, vous avez du oublier quelque chose 🧐');
            return;
        }

        jamments.postComment(slug, comment, name, email, parentId)
        .then(() => {
            showSuccessNotif('Votre commentaire a été soumis avec succès');
            commentInput.value = '';
        })
        .catch(showGenericErrMsg);

        saveUserLocally({ name, email });
    });
}

/**
 * Validate comment by sending validation data to API and refresh commentlist after that
 * @param { String } url
 * @param { HTMLElement } commentSection
 * @param { String } md5Email
 * @param { String } secret
 * @param { Boolean } isAdmin
 */
function validateComment(url, commentSection, md5Email, secret, isAdmin) {
    const params = new URLSearchParams(url.search.substring(1));

    if (!params.get('validate_comment')) return;

    const commentId = params.get('comment_id');
    const userMd5Email = params.get('md5_email');
    const userSecret = params.get('user_secret');

    jamments.validateComment(commentId, userSecret)
    .then(() => {
        showSuccessNotif('Votre commentaire est maintenant validé 🎉');
        saveUserLocally({ md5Email: userMd5Email, secret: userSecret });

        setTimeout(() => insertComments(md5Email, secret, isAdmin, url.pathname, commentSection), 2000);
        window.history.replaceState(null, null, url.href.split('?', 1)[0]);
    })
    .catch(showGenericErrMsg);
}

/**
 * Set number of comments for each article if any
 * @param { HTMLElement } commentsInfoElements
 * @param { Object } siteInfos
 */
function setCommentsCount(commentsInfoElements, siteInfos) {
    commentsInfoElements.forEach((el) => {
        const url = getAttribute(el, 'data-url');
        const slug = getAttribute(el, 'data-slug').replace(/^\/|\/$/g, '');
        const commentsNumber = siteInfos.commentsCount[slug];

        if (commentsNumber && commentsNumber === 1 && url) setHTML(el, `<a href="/${slug}/#comments-section">${siteInfos.commentsCount[slug]} commentaire</a>`);
        else if (commentsNumber && commentsNumber === 1) setHTML(el, `${siteInfos.commentsCount[slug]} commentaire`);
        else if (commentsNumber && commentsNumber > 1 && url) setHTML(el, `<a href="/${slug}/#comments-section">${siteInfos.commentsCount[slug]} commentaires</a>`);
        else if (commentsNumber && commentsNumber > 1) setHTML(el, `${siteInfos.commentsCount[slug]} commentaires`);
    });
}

/**
 * Load comments and init commenting capability
 * @param { String } url
 * @param { HTMLElement } commentsInfoElements
 * @param { HTMLElement } commentSection
 * @param { HTMLFormElement } commentForm
 * @param { HTMLInputElement } commentInput
 * @param { HTMLInputElement } nameInput
 * @param { HTMLInputElement } emailInput
 */
function initComments(url, commentsInfoElements, commentSection, commentForm, commentInput, nameInput, emailInput) {
    if (!commentsInfoElements) return;

    const md5Email = localStorage.getItem('md5Email');
    const secret = localStorage.getItem('secret');

    jamments.getSiteInfos()
    .then((siteInfos) => {
        setCommentsCount(commentsInfoElements, siteInfos);

        if (!commentSection) return;
        preFillCommentForm(nameInput, emailInput);

        insertComments(md5Email, secret, checkIfUserIsAdmin(siteInfos.md5_admin_email, md5Email), url.pathname, commentSection)
        .then(() => {
            initCommentsControls(commentSection, commentForm, commentInput, secret);

            // call initPostComment afterwards to bind events on dynamically inserted "answer" buttons
            initPostComment(url.pathname, commentSection, commentForm, commentInput, nameInput, emailInput);
            validateComment(url, commentSection, md5Email, secret, checkIfUserIsAdmin(siteInfos.md5_admin_email, md5Email));
        });
    })
    .catch(showGenericErrMsg);
}

/**
 * Unsubscribe user from new comments on an article
 * @param { String } url
 */
function unsubscribeFromNewCommentsNotifications(url) {
    const params = new URLSearchParams(url.search.substring(1));

    if (!params.get('unsubscribe_from_comments_notif')) return;

    jamments.updateNewCommentsSubscription(params.get('article_id'), params.get('user_id'), params.get('user_secret'), false)
    .then(() => showSuccessNotif('Vous ne recevrez plus d’email pour cet article'))
    .catch(showGenericErrMsg);
}

/**
 * Add smooth scrolling to ToC links
 * @param { NodeList } tocLinks
 */
function smoothScrollToc(tocLinks) {
    onAll(tocLinks, 'click', (e) => {
        e.preventDefault();

        const anchor = getAttribute(getParent(e.target), 'href');
        const target = find(anchor);

        window.history.pushState(null, null, anchor);
        target.scrollIntoView({ behavior: 'smooth' });
    });
}

/**
 * Compute needed modal transform to adopt btn's size & position
 * @param { HTMLElement } btn
 * @param { HTMLElement } modal
 * @return { String } CSS transform's translate property
 */
function getModalInitialTransform(btn, modal) {
    const btnWidth = getWidth(btn);
    const btnHeight = getHeight(btn);
    const btnTop = getCoordinates(btn, 'top');
    const btnLeft = getCoordinates(btn, 'left');

    const modalWidth = getWidth(modal);
    const modalHeight = getHeight(modal);
    const modalTop = getCoordinates(modal, 'top');
    const modalLeft = getCoordinates(modal, 'left');

    const scaleWidth = btnWidth / modalWidth;
    const scaleHeight = btnHeight / modalHeight;
    const modalTranslateX = btnLeft - modalLeft;
    const modalTranslateY = btnTop - modalTop;

    return `translate(${modalTranslateX}px, ${modalTranslateY}px) scale(${scaleWidth}, ${scaleHeight})`;
}

/**
 * Manage expanding/closing cheatsheet title into modal on click
 * @param { HTMLBodyElement } body
 * @param { NodeList } titles
 * @param { HTMLElement } overlay
 */
function initMorphCheatSheet(body, titles, overlay) {
    onAll(titles, 'click', (e) => {
        preventDefault(e);

        const sheetTitle = getEventTarget(e);
        const sheet = find(getAttribute(getEventTarget(e), 'href'));
        const content = findChild(sheet, '.content');
        const closeBtn = document.createElement('button');

        addClass(closeBtn, 'close');
        setHTML(closeBtn, 'x');
        prepend(content, closeBtn);

        // as soon as btn is faded, trigger modal expansion
        once(sheetTitle, 'transitionend', () => {
            setStyle(sheet, 'transform', 'translate(0) scale(1)');
            addClass(sheet, 'expanded');
            addClass(sheetTitle, 'visible');
            addClass(overlay, 'visible');
        });

        addClass(body, 'noscroll');
        addClass(overlay, 'active');
        addClass(sheet, 'active');
        setStyle(sheet, 'height', `${getHeight(sheet)}px`);
        setStyle(sheet, 'bottom', 0);

        const initialState = getModalInitialTransform(sheetTitle, sheet);
        setStyle(sheet, 'transform', initialState);

        // give the browser just enough time to position the modal
        // then set visible
        setTimeout(() => ['fading', 'visible'].forEach(className => (addClass(sheet, className))), 100);
        ['hidden', 'active'].forEach(className => addClass(sheetTitle, className));

        on(closeBtn, 'click', () => {
            // once modal faded, return it to totally inactive state
            once(sheet, 'transitionend', () => {
                removeClass(sheet, 'fading');
                ['visible', 'active'].forEach(className => removeClass(sheet, className));
                removeClass(sheetTitle, 'active');
                setStyle(sheet, 'transform', '');
                removeClass(sheetTitle, 'hidden');
                removeClass(overlay, 'active');
            });

            removeClass(body, 'noscroll');
            removeClass(overlay, 'visible');
            removeClass(sheet, 'expanded');
            setStyle(sheet, 'transform', initialState);
        });
    });
}

ready(() => {
    hljs.initHighlighting();
    smoothScrollToc(findAll('.post .toc a'));

    initSearch(
        find('#searchform input'),
        find('#searchform .results')
    );

    initComments(
        window.location,
        findAll('.comments-infos'),
        find('#comments'),
        find('#comments-form'),
        find('#comment'),
        find('#name'),
        find('#email')
    );

    unsubscribeFromNewCommentsNotifications(window.location);

    // initMorphCheatSheet(
    //     find('body'),
    //     findAll('.sheets > ul li'),
    //     find('#overlay')
    // );
});
